import { ClerkProvider } from "@clerk/react";
import { type FC, type ReactNode } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { Provider } from "react-redux";
import { Outlet, useNavigate, useNavigation } from "react-router";
import { ToastContainer } from "react-toastify";

import {
  CLERK_PUBLISHABLE_KEY,
  GOOGLE_RECAPTCHA_SITE_KEY,
} from "~/constants/common";
import { ROUTES } from "~/constants/routing";
import { InitFirstTouch } from "~/feature/attribution/InitFirstTouch";
import { AuthModalBridge } from "~/feature/auth/AuthModalBridge";
import { InitClerkToken } from "~/feature/auth/InitClerkToken";
import { InitCategories } from "~/feature/categories/InitCategories";
import { DashboardViewProvider } from "~/feature/dashboard/providers/DashboardViewProvider";
import { EventPageSkeleton } from "~/feature/event/components/EventPageSkeleton/EventPageSkeleton";
import { GetUpdatesAutoDialog } from "~/feature/getUpdates/components/GetUpdatesAutoDialog";
import { GetUpdatesCta } from "~/feature/getUpdates/components/GetUpdatesCta";
import { InitSavedFilters } from "~/feature/savedFilters/InitSavedFilters";
import { SyncSubscription } from "~/feature/subscription/SyncSubscription";
import { IntercomInit } from "~/feature/support/IntercomInit";
import { WatchlistProvider } from "~/feature/watchlist/providers/WatchlistProvider";
import { AppNavigationProvider } from "~/providers/AppNavigationProvider";
import { CategoriesProvider } from "~/providers/CategoriesProvider";
import { FilterProvider } from "~/providers/FilterProvider";
import { SavedFiltersProvider } from "~/providers/SavedFiltersProvider";
import { ThemeProvider } from "~/providers/ThemeProvider";
import { AppUserProvider } from "~/providers/UserProvider";
import { store } from "~/store";

import * as Styled from "./AppLayout.styles";

interface IAppLayoutPros {
  metricsLine: ReactNode;
  appBar: ReactNode;
  footer: ReactNode;
}

const EVENT_ROUTE_RE = /^\/event\//;

const NavigationAwareOutlet: FC = () => {
  const navigation = useNavigation();
  const isNavigatingToEvent =
    navigation.state === "loading" &&
    EVENT_ROUTE_RE.test(navigation.location?.pathname ?? "");

  if (isNavigatingToEvent) {
    return <EventPageSkeleton />;
  }

  return <Outlet />;
};

const toRelativePath = (to: string): string => {
  try {
    const url = new URL(to);
    if (url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash;
    }
  } catch {
    return to;
  }
  return to;
};

export const AppLayout: FC<IAppLayoutPros> = ({
  metricsLine,
  appBar,
  footer,
}) => {
  const navigate = useNavigate();

  return (
    <Provider store={store}>
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        afterSignOutUrl={ROUTES.main.createURL()}
        routerPush={(to) => navigate(toRelativePath(to))}
        routerReplace={(to) => navigate(toRelativePath(to), { replace: true })}
      >
        <AppUserProvider>
          <FilterProvider>
            <AppNavigationProvider>
              <InitFirstTouch />
              <InitClerkToken />
              <AuthModalBridge />
              <SyncSubscription />
              <ThemeProvider>
                <CategoriesProvider>
                  <WatchlistProvider>
                    <SavedFiltersProvider>
                      <InitSavedFilters />
                      <InitCategories />
                      <GoogleReCaptchaProvider
                        reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY}
                        scriptProps={{
                          async: true,
                          defer: true,
                          appendTo: "body",
                        }}
                      >
                        <Styled.Root>
                          <IntercomInit />
                          <GetUpdatesCta />
                          <GetUpdatesAutoDialog />
                          <Styled.MainWrapper>
                            <section>
                              {metricsLine}
                              {appBar}
                            </section>
                            <DashboardViewProvider>
                              <NavigationAwareOutlet />
                            </DashboardViewProvider>
                          </Styled.MainWrapper>
                          <Styled.FooterWrapper $clearMobileBar={!!appBar}>
                            {footer}
                          </Styled.FooterWrapper>
                        </Styled.Root>
                      </GoogleReCaptchaProvider>
                    </SavedFiltersProvider>
                  </WatchlistProvider>
                </CategoriesProvider>
                <ToastContainer />
              </ThemeProvider>
            </AppNavigationProvider>
          </FilterProvider>
        </AppUserProvider>
      </ClerkProvider>
    </Provider>
  );
};
